import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { formPreviewDoc, useForm, useUpdateForm, useFormHtml } from '@wordpuppi/shared';
import type { CreateFormData } from '@wordpuppi/shared';
import { FormBuilder } from '@/components/FormBuilder';
import { AddToSitePanel } from '@/components/AddToSitePanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useState } from 'react';

export const Route = createFileRoute('/sites/$siteSlug/forms/$formId/')({
  component: EditFormPage,
});

function EditFormPage() {
  const { siteSlug, formId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useForm(siteSlug, formId);
  const updateForm = useUpdateForm(siteSlug);
  const { data: htmlData, refetch: refetchHtml } = useFormHtml(siteSlug, formId);

  const [showPreview, setShowPreview] = useState(false);

  if (isLoading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading form...
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Form not found
      </div>
    );
  }

  const form = data.data;

  const initialData: CreateFormData = {
    name: form.name,
    slug: form.slug,
    description: form.description,
    settings: {
      submit_label: form.settings?.submit_label,
      success_message: form.settings?.success_message,
      redirect_url: form.settings?.redirect_url,
      action_url: form.settings?.action_url,
      notify_emails: form.settings?.notify_emails,
      send_confirmation: form.settings?.send_confirmation,
      webhook_url: form.settings?.webhook_url,
      enable_turnstile: form.settings?.enable_turnstile,
      add_to_members: form.settings?.add_to_members,
    },
    steps: form.steps.map((step) => ({
      name: step.name,
      description: step.description,
      order: step.order,
      fields: step.fields.map((field) => ({
        name: field.name,
        label: field.label,
        field_type: field.field_type,
        order: field.order,
        required: field.required,
        placeholder: field.placeholder,
        help_text: field.help_text,
        default_value: field.default_value,
        validation_rules: field.validation_rules,
        options: field.options.map((opt) => ({
          label: opt.label,
          value: opt.value,
          order: opt.order,
        })),
      })),
    })),
  };

  const handleSubmit = async (formData: CreateFormData) => {
    try {
      await updateForm.mutateAsync({ formId, data: formData });
      toast.success('Form updated');
      refetchHtml();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update form');
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Edit Form</h1>
          <p className="text-muted-foreground mt-1">
            {form.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" asChild>
            <Link
              to="/sites/$siteSlug/forms/$formId/submissions"
              params={{ siteSlug, formId }}
            >
              Submissions
            </Link>
          </Button>
          <Button variant="secondary" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/forms', params: { siteSlug } })}
          >
            Back to Forms
          </Button>
        </div>
      </div>

      {/* Add this form to a page / copy the embed marker */}
      <AddToSitePanel siteSlug={siteSlug} formSlug={form.slug} />

      {/* AB#660: the form as the site renders it (the backend ships its own
          styles with include_styles). Sandboxed with NO allow-forms and NO
          allow-scripts, so the preview can't submit and can't touch the admin:
          fields are still interactive, Turnstile and multi-step JS are not. */}
      {showPreview && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Form preview</CardTitle>
          </CardHeader>
          <CardContent>
            {htmlData?.data?.html ? (
              <iframe
                title="Form preview"
                sandbox=""
                srcDoc={formPreviewDoc(htmlData.data.html)}
                // ponytail: fixed height — auto-sizing needs allow-scripts, which
                // is exactly what keeps this preview from submitting anything.
                className="h-[28rem] w-full rounded-lg border bg-white"
              />
            ) : (
              <p className="text-muted-foreground text-sm">Rendering…</p>
            )}
          </CardContent>
        </Card>
      )}

      <FormBuilder
        initialData={initialData}
        isEditing
        onSubmit={handleSubmit}
        isSubmitting={updateForm.isPending}
        submitLabel="Save Changes"
      />
    </div>
  );
}
